import { NextRequest, NextResponse } from 'next/server'
import { and, count, eq } from 'drizzle-orm'
import { db } from '@/db'
import { auditLog, knowledgeEntries, knowledgeEntryRevisions } from '@/db/schema'
import { requireExpertApiUser } from '@/lib/role-guard'
import { inngest } from '@/lib/inngest'
import { normalizeKnowledgeInput } from '@/lib/knowledge'
import type { KnowledgeEntryKind } from '@/db/schema'

type KnowledgeUpdateBody = {
  title?: unknown
  slug?: unknown
  kind?: unknown
  bodyMarkdown?: unknown
  changeReason?: unknown
}

const VALID_KINDS = new Set<KnowledgeEntryKind>(['index', 'log', 'entity', 'concept', 'procedure'])

export async function GET(
  _request: NextRequest,
  { params }: { params: Promise<{ id: string }> },
) {
  const guard = await requireExpertApiUser()
  if (!guard.ok) return guard.response

  const { id } = await params
  const [entry] = await db
    .select()
    .from(knowledgeEntries)
    .where(and(eq(knowledgeEntries.id, id), eq(knowledgeEntries.tenantId, guard.user.tenantId), eq(knowledgeEntries.isDeleted, false)))
    .limit(1)

  if (!entry) return NextResponse.json({ error: 'Knowledge entry not found' }, { status: 404 })
  return NextResponse.json({ entry })
}

export async function PUT(
  request: NextRequest,
  { params }: { params: Promise<{ id: string }> },
) {
  const guard = await requireExpertApiUser()
  if (!guard.ok) return guard.response

  const { id } = await params
  const existingRows = await db
    .select()
    .from(knowledgeEntries)
    .where(and(eq(knowledgeEntries.id, id), eq(knowledgeEntries.tenantId, guard.user.tenantId), eq(knowledgeEntries.isDeleted, false)))
    .limit(1)

  const existing = existingRows[0]
  if (!existing) return NextResponse.json({ error: 'Knowledge entry not found' }, { status: 404 })

  const body = (await request.json().catch(() => null)) as KnowledgeUpdateBody | null

  try {
    const normalized = normalizeKnowledgeInput({
      title: typeof body?.title === 'string' ? body.title : existing.title,
      slug: typeof body?.slug === 'string' ? body.slug : existing.slug,
      kind: parseKind(body?.kind) ?? existing.kind,
      bodyMarkdown: typeof body?.bodyMarkdown === 'string' ? body.bodyMarkdown : existing.bodyMarkdown,
    })

    const [entry] = await db
      .update(knowledgeEntries)
      .set({
        slug: normalized.slug,
        title: normalized.title,
        kind: normalized.kind,
        bodyMarkdown: normalized.bodyMarkdown,
        flatMarkdown: normalized.flatMarkdown,
        updatedById: guard.user.id,
        embedding: null,
        embeddingModel: null,
        embeddingUpdatedAt: null,
        updatedAt: new Date(),
      })
      .where(and(eq(knowledgeEntries.id, id), eq(knowledgeEntries.tenantId, guard.user.tenantId)))
      .returning()

    const [{ value: revisionCount }] = await db
      .select({ value: count() })
      .from(knowledgeEntryRevisions)
      .where(eq(knowledgeEntryRevisions.entryId, id))

    await db.insert(knowledgeEntryRevisions).values({
      tenantId: guard.user.tenantId,
      entryId: entry.id,
      revisionNumber: Number(revisionCount) + 1,
      title: entry.title,
      bodyMarkdown: entry.bodyMarkdown,
      flatMarkdown: entry.flatMarkdown,
      editedById: guard.user.id,
      changeReason: typeof body?.changeReason === 'string' ? body.changeReason : 'updated',
    })

    await db.insert(auditLog).values({
      tenantId: guard.user.tenantId,
      actorId: guard.user.id,
      actorType: 'user',
      action: 'knowledge.updated',
      entityType: 'knowledge_entry',
      entityId: entry.id,
      payload: { slug: entry.slug, kind: entry.kind },
    })

    try {
      await inngest.send({
        name: 'knowledge/entry.changed',
        data: { entryId: entry.id, tenantId: guard.user.tenantId, action: 'updated' },
      })
    } catch {
      // Inngest unavailable — indexing will not run until re-triggered
    }

    return NextResponse.json({ entry })
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unable to update knowledge entry'
    const status = message.includes('duplicate key') ? 409 : 400
    return NextResponse.json({ error: message }, { status })
  }
}

export async function DELETE(
  _request: NextRequest,
  { params }: { params: Promise<{ id: string }> },
) {
  const guard = await requireExpertApiUser()
  if (!guard.ok) return guard.response

  const { id } = await params

  const [entry] = await db
    .select({ id: knowledgeEntries.id, slug: knowledgeEntries.slug, isDeleted: knowledgeEntries.isDeleted })
    .from(knowledgeEntries)
    .where(and(eq(knowledgeEntries.id, id), eq(knowledgeEntries.tenantId, guard.user.tenantId)))
    .limit(1)

  if (!entry) return NextResponse.json({ error: 'Knowledge entry not found' }, { status: 404 })
  if (entry.isDeleted) return NextResponse.json({ error: 'Already deleted' }, { status: 409 })

  await db
    .update(knowledgeEntries)
    .set({ isDeleted: true, embedding: null, embeddingModel: null, embeddingUpdatedAt: null, updatedAt: new Date() })
    .where(and(eq(knowledgeEntries.id, id), eq(knowledgeEntries.tenantId, guard.user.tenantId)))

  await db.insert(auditLog).values({
    tenantId: guard.user.tenantId,
    actorId: guard.user.id,
    actorType: 'user',
    action: 'knowledge.deleted',
    entityType: 'knowledge_entry',
    entityId: id,
    payload: { slug: entry.slug },
  })

  return new NextResponse(null, { status: 204 })
}

function parseKind(value: unknown): KnowledgeEntryKind | undefined {
  if (typeof value !== 'string') return undefined
  if (!VALID_KINDS.has(value as KnowledgeEntryKind)) throw new Error('Invalid knowledge entry kind')
  return value as KnowledgeEntryKind
}
